import { useEffect, useRef, useState } from 'react';

import {
  frameUrl,
  normalizeAttention,
  postToFrame,
  readTabMessage,
  tabCycleDelta,
  type FrameMessage,
} from '../lib/embed.js';
import { useAuth } from '../state/auth.js';
import { useProjectTabs } from '../state/project-tabs.js';
import { Icon } from './ui/icons.js';

/**
 * The tab host: a strip of project tabs plus one same-origin iframe per open
 * project. Each frame is a complete copy of the app in embedded mode (see
 * lib/embed.ts), so every project keeps its own stores, terminals and engine
 * views. Frames mount on first visit and then stay mounted while hidden.
 */

/** Mounted frames by project id, for postMessage and focus. */
const frames = new Map<number, HTMLIFrameElement>();

/** Pending `prepare-close` round trips by project id. */
const closeWaiters = new Map<number, (dirty: boolean) => void>();

/** How long a frame gets to flush its saves before we fall back to its last report. */
const CLOSE_TIMEOUT_MS = 3000;

function projectForSource(source: MessageEventSource | null): number | null {
  if (!source) return null;
  for (const [projectId, frame] of frames) {
    if (frame.contentWindow === source) return projectId;
  }
  return null;
}

/** Forward a native menu command to the visible project, if any. */
export function forwardToActiveTab(command: string): boolean {
  const active = useProjectTabs.getState().active;
  if (active === null) return false;
  const frame = frames.get(active);
  if (!frame) return false;
  postToFrame(frame, { type: 'menu', command });
  return true;
}

/**
 * Close a project tab: let its frame flush pending saves, then confirm if
 * anything is still unsaved.
 */
export async function requestCloseTab(projectId: number): Promise<void> {
  const tab = useProjectTabs.getState().tabs.find((t) => t.projectId === projectId);
  if (!tab || closeWaiters.has(projectId)) return;

  const frame = frames.get(projectId);
  let dirty = useProjectTabs.getState().dirty[projectId] ?? false;
  if (frame?.contentWindow) {
    dirty = await new Promise<boolean>((resolve) => {
      const finish = (value: boolean) => {
        window.clearTimeout(timer);
        closeWaiters.delete(projectId);
        resolve(value);
      };
      const timer = window.setTimeout(
        () => finish(useProjectTabs.getState().dirty[projectId] ?? false),
        CLOSE_TIMEOUT_MS,
      );
      closeWaiters.set(projectId, finish);
      postToFrame(frame, { type: 'prepare-close' });
    });
  }

  if (dirty && !window.confirm(`"${tab.name}" has unsaved changes. Close it anyway?`)) return;
  useProjectTabs.getState().remove(projectId);
}

let authCheck: Promise<void> | null = null;

/** A frame lost its session: re-check once, which shows login if it really expired. */
function recheckAuth(): void {
  if (authCheck) return;
  authCheck = useAuth
    .getState()
    .bootstrap()
    .finally(() => {
      authCheck = null;
    });
}

/** Window-level wiring for the host: frame messages, tab shortcuts, unload guard. */
export function useTabHost(): void {
  useEffect(() => {
    const origin = window.location.origin;

    const onMessage = (event: MessageEvent) => {
      const message = readTabMessage<FrameMessage>(event, origin);
      if (!message) return;
      const projectId = projectForSource(event.source);
      if (projectId === null) return;
      const tabs = useProjectTabs.getState();

      switch (message.type) {
        case 'state':
          tabs.report(projectId, String(message.name ?? ''), message.dirty === true);
          break;
        case 'close':
          void requestCloseTab(projectId);
          break;
        case 'close-ready':
          closeWaiters.get(projectId)?.(message.dirty === true);
          break;
        case 'cycle':
          tabs.cycle(message.delta === -1 ? -1 : 1);
          break;
        case 'auth-lost':
          recheckAuth();
          break;
        case 'attention':
          tabs.setAttention(projectId, normalizeAttention(message.count));
          break;
        default:
          break;
      }
    };

    const onKey = (event: KeyboardEvent) => {
      const delta = tabCycleDelta(event);
      if (!delta) return;
      event.preventDefault();
      event.stopPropagation();
      useProjectTabs.getState().cycle(delta);
    };

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (useProjectTabs.getState().anyDirty()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);
}

const DRAG_TYPE = 'application/x-ai-studio-tab';

/** The tab strip. `titlebar` sits inside the desktop title bar; `bar` is the web strip. */
export function ProjectTabStrip({ variant }: { variant: 'titlebar' | 'bar' }) {
  const tabs = useProjectTabs((s) => s.tabs);
  const active = useProjectTabs((s) => s.active);
  const dirty = useProjectTabs((s) => s.dirty);
  const attention = useProjectTabs((s) => s.attention);
  const focus = useProjectTabs((s) => s.focus);
  const move = useProjectTabs((s) => s.move);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  return (
    <div className={`ptabs ptabs-${variant}`} role="tablist" aria-label="Open projects">
      <button
        type="button"
        role="tab"
        aria-selected={active === null}
        className={`ptab ptab-home${active === null ? ' is-active' : ''}`}
        title="Projects"
        onClick={() => focus(null)}
      >
        <Icon name="folder" size={13} />
        <span className="ptab-label">Projects</span>
      </button>

      {tabs.map((tab, index) => {
        const isActive = active === tab.projectId;
        const isDirty = dirty[tab.projectId] ?? false;
        const waiting = attention[tab.projectId] ?? 0;
        const waitingNote = waiting > 0 ? ` (${waiting} waiting)` : '';
        return (
          <div
            key={tab.projectId}
            role="tab"
            tabIndex={0}
            aria-selected={isActive}
            className={`ptab${isActive ? ' is-active' : ''}${dropIndex === index ? ' is-drop' : ''}${waiting > 0 ? ' has-attention' : ''}`}
            title={`${isDirty ? `${tab.name} (unsaved changes)` : tab.name}${waitingNote}`}
            aria-label={`${tab.name}${waitingNote}`}
            draggable
            onClick={() => focus(tab.projectId)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                focus(tab.projectId);
              }
            }}
            onAuxClick={(event) => {
              // Middle-click closes, as in browsers and editors.
              if (event.button === 1) {
                event.preventDefault();
                void requestCloseTab(tab.projectId);
              }
            }}
            onDragStart={(event) => {
              event.dataTransfer.setData(DRAG_TYPE, String(tab.projectId));
              event.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (dropIndex !== index) setDropIndex(index);
            }}
            onDragLeave={() => setDropIndex((current) => (current === index ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              setDropIndex(null);
              const id = Number(event.dataTransfer.getData(DRAG_TYPE));
              if (Number.isSafeInteger(id) && id > 0) move(id, index);
            }}
            onDragEnd={() => setDropIndex(null)}
          >
            <span className="ptab-label">{tab.name}</span>
            {waiting > 0 && (
              <span className="ptab-attention" aria-hidden="true">
                {waiting}
              </span>
            )}
            <button
              type="button"
              className={`ptab-close${isDirty ? ' is-dirty' : ''}`}
              title="Close project"
              aria-label={`Close ${tab.name}`}
              onClick={(event) => {
                event.stopPropagation();
                void requestCloseTab(tab.projectId);
              }}
            >
              <span className="ptab-dot" aria-hidden="true" />
              <Icon name="x" size={11} className="ptab-x" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className="ptab-new"
        title="Open another project"
        aria-label="Open another project"
        onClick={() => focus(null)}
      >
        <Icon name="plus" size={13} />
      </button>
    </div>
  );
}

/** One iframe per visited project tab; only the active one is visible. */
export function ProjectFrames() {
  const tabs = useProjectTabs((s) => s.tabs);
  const active = useProjectTabs((s) => s.active);
  // Restored tabs mount lazily, so a reload with many tabs doesn't boot every engine at once.
  const [mounted, setMounted] = useState<ReadonlySet<number>>(
    () => new Set(active === null ? [] : [active]),
  );
  const previous = useRef<number | null>(null);

  useEffect(() => {
    setMounted((prev) => {
      const open = new Set(tabs.map((t) => t.projectId));
      const next = new Set([...prev].filter((id) => open.has(id)));
      if (active !== null) next.add(active);
      return next.size === prev.size && [...next].every((id) => prev.has(id)) ? prev : next;
    });
  }, [tabs, active]);

  // Hand keyboard focus to the visible frame so its shortcuts work right away.
  useEffect(() => {
    // The tab that was on screen is hidden now (it pauses "follow assistant").
    if (previous.current !== null && previous.current !== active) {
      postToFrame(frames.get(previous.current), { type: 'deactivated' });
    }
    if (active === null) {
      previous.current = null;
      return;
    }
    if (previous.current === active) return;
    const frame = frames.get(active);
    // Not mounted yet: this runs again once `mounted` includes it.
    if (!frame) return;
    previous.current = active;
    postToFrame(frame, { type: 'activated' });
    frame.contentWindow?.focus();
  }, [active, mounted]);

  const base = import.meta.env.BASE_URL;

  return (
    <>
      {tabs
        .filter((tab) => mounted.has(tab.projectId))
        .map((tab) => (
          <iframe
            key={tab.projectId}
            ref={(element) => {
              if (element) frames.set(tab.projectId, element);
              else frames.delete(tab.projectId);
            }}
            className="project-frame"
            title={tab.name}
            src={frameUrl(base, tab.projectId)}
            hidden={active !== tab.projectId}
            allow="fullscreen; clipboard-read; clipboard-write; gamepad; autoplay"
            allowFullScreen
          />
        ))}
    </>
  );
}
